// Rendu du chemin code-barres (Open Food Facts) — déplacé tel quel depuis main.ts, qui orchestre
// désormais aussi la capture photo et le chemin modèle. Rien n'a changé au comportement : toute
// valeur d'origine OFF reste échappée avant interpolation (web/src/htmlSafety.ts).
import { resolveGrams, macrosForGrams } from '../barcode/weightResolution.js';
import type { OffLookupResult, OffProduct } from '../barcode/types.js';
import { escapeHtml, safeImageSrc } from '../htmlSafety.js';

export function renderLookupResult(ean: string, lookup: OffLookupResult, resultEl: HTMLElement): void {
  if (lookup.status === 'found') {
    renderFoundProduct(lookup.product, resultEl);
    return;
  }

  // `lookup.productName`, `ean` (lu sur l'image scannée) et `lookup.contributeUrl` sont traités
  // comme non fiables : échappés avant interpolation dans le innerHTML (voir htmlSafety.ts).
  const message =
    lookup.status === 'incomplete'
      ? `Fiche ${escapeHtml(lookup.productName ?? ean)} trouvée sur Open Food Facts, mais sans
         composition nutritionnelle exploitable.`
      : `Code-barres ${escapeHtml(ean)} inconnu d'Open Food Facts.`;

  resultEl.innerHTML = `
    <p>${message}</p>
    <p>La photo va être analysée par le modèle.</p>
    <p><a href="${escapeHtml(lookup.contributeUrl)}" target="_blank" rel="noopener noreferrer">
      Contribuer la fiche à Open Food Facts</a></p>
  `;
}

export function renderFoundProduct(product: OffProduct, resultEl: HTMLElement): void {
  // Toute valeur issue d'Open Food Facts (nom, image, Nutri-Score) est échappée avant interpolation
  // dans le innerHTML — OFF est éditable par n'importe qui (htmlSafety.ts). `imageUrl` est en plus
  // validé en `https:` uniquement avant d'être utilisé comme `src`.
  const imageSrc = safeImageSrc(product.imageUrl);
  const initialGrams = resolveGrams(product, null);

  resultEl.innerHTML = `
    <article class="product-card">
      ${imageSrc ? `<img src="${escapeHtml(imageSrc)}" alt="" width="96" height="96" />` : ''}
      <h2>${escapeHtml(product.productName ?? 'Produit sans nom')}</h2>
      ${product.nutriscoreGrade ? `<p>Nutri-Score : ${escapeHtml(product.nutriscoreGrade.toUpperCase())}</p>` : ''}
      <p class="cost-note">Composition exacte — Open Food Facts, 0 appel au modèle.</p>
      <h3>Pour 100 g</h3>
      ${renderMacrosTable(product.nutriments)}
      <label>Poids en grammes
        <input type="number" id="manual-grams" min="1" step="1" inputmode="numeric"
          ${initialGrams !== null ? `value="${initialGrams}"` : ''} />
      </label>
      <div id="grams-result"></div>
    </article>
  `;

  // Le champ de poids reste affiché et modifiable dans les deux cas (serving_size connu ou pas,
  // objectif § 5) : il n'est jamais remplacé par le résultat, contrairement à la version précédente
  // (revue PR #549, thread 1317, point secondaire).
  const input = resultEl.querySelector<HTMLInputElement>('#manual-grams')!;
  const gramsResultEl = resultEl.querySelector<HTMLDivElement>('#grams-result')!;

  const updateGramsResult = (): void => {
    const typed = Number(input.value);
    const grams = resolveGrams(product, Number.isFinite(typed) && typed > 0 ? typed : null);
    if (grams === null) {
      gramsResultEl.innerHTML = `<p>Portion inconnue : saisir un poids pour convertir, ou laisser le
        modèle l'estimer une fois la photo envoyée.</p>`;
      return;
    }
    const macros = macrosForGrams(product.nutriments, grams);
    const servingNote =
      grams === product.servingGrams && product.servingSize ? ` (${escapeHtml(product.servingSize)})` : '';
    gramsResultEl.innerHTML = `<h3>Pour ${grams} g${servingNote}</h3>${macros ? renderMacrosTable(macros) : ''}`;
  };

  input.addEventListener('input', updateGramsResult);
  updateGramsResult();
}

function renderMacrosTable(macros: OffProduct['nutriments']): string {
  return `
    <ul class="macros">
      <li>Énergie : ${macros.kcal} kcal</li>
      <li>Glucides : ${macros.glucides_g} g</li>
      <li>dont sucres : ${macros.sucres_g} g</li>
      <li>Protéines : ${macros.proteines_g} g</li>
      <li>Lipides : ${macros.lipides_g} g</li>
      <li>Fibres : ${macros.fibres_g} g</li>
      <li>Sel : ${macros.sel_g} g</li>
    </ul>
  `;
}
